import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { buildFlightContext } from 'tripPlanner/components/Chat/Flights/buildFlightContext'
import { compactLines } from './message'

const MAX_FLIGHT_RESULTS_IN_CONTEXT = 5

interface BuildFlightSearchDrawerContextArgs {
  intentParams: FlightSearchParams
  searchParams?: FlightSearchParams
  hasSearched: boolean
  fetching: boolean
  hasError: boolean
  activeSegmentLabel: string
  sort: string
  activeFilterCount: number
  displayJourneys: Array<App.JourneyV2>
  visibleJourneys: Array<App.JourneyV2>
  selectedFlights: Record<string, App.JourneyV2>
  searchId?: string
  sessionId?: string
  searchExpiry?: number
}

interface FlightItineraryLeg {
  departureAirport?: string
  arrivalAirport?: string
  departureDate?: string
}

function formatFlightItinerary(itinerary: Array<FlightItineraryLeg>): string {
  if (!itinerary.length) return 'No complete itinerary selected'
  return itinerary
    .map((leg, index) => {
      const route = `${leg.departureAirport || '?'} -> ${leg.arrivalAirport || '?'}`
      const date = leg.departureDate ? ` on ${leg.departureDate}` : ''
      return `Leg ${index + 1}: ${route}${date}`
    })
    .join('; ')
}

function formatFlightSearchParams(params: FlightSearchParams): string {
  return compactLines([
    `Itinerary: ${formatFlightItinerary(params.itinerary)}`,
    `Fare type: ${params.fareType ?? 'oneWay'}`,
    `Fare class: ${params.fareClass ?? 'economy'}`,
    `Occupancies: ${JSON.stringify(params.occupancies)}`,
  ])
}

function formatSelectedFlights(
  selectedFlights: Record<string, App.JourneyV2>,
): string | undefined {
  const entries = Object.entries(selectedFlights)
  if (!entries.length) return undefined

  return entries
    .map(([segment, journey]) => `${segment}:\n${buildFlightContext(journey)}`)
    .join('\n\n')
}

function formatFlightResultSummaries(journeys: Array<App.JourneyV2>): string {
  if (!journeys.length) return 'No visible flight results'

  return journeys
    .slice(0, MAX_FLIGHT_RESULTS_IN_CONTEXT)
    .map((journey, index) => `Result ${index + 1}:\n${buildFlightContext(journey)}`)
    .join('\n\n')
}

export function buildFlightSearchDrawerContext({
  intentParams,
  searchParams,
  hasSearched,
  fetching,
  hasError,
  activeSegmentLabel,
  sort,
  activeFilterCount,
  displayJourneys,
  visibleJourneys,
  selectedFlights,
  searchId,
  sessionId,
  searchExpiry,
}: BuildFlightSearchDrawerContextArgs): string {
  const selectedFlightSummary = formatSelectedFlights(selectedFlights)
  const visibleResultsSummary = hasSearched
    ? formatFlightResultSummaries(visibleJourneys)
    : undefined

  return compactLines([
    'Drawer: Flight search',
    `Status: ${fetching ? 'searching' : hasError ? 'error' : hasSearched ? 'searched' : 'not searched'}`,
    `Current search intent:\n${formatFlightSearchParams(intentParams)}`,
    searchParams && `Submitted search:\n${formatFlightSearchParams(searchParams)}`,
    hasSearched && `Active segment: ${activeSegmentLabel}`,
    hasSearched && `Sort: ${sort}`,
    hasSearched && `Active filter count: ${activeFilterCount}`,
    hasSearched && `Results before filters: ${displayJourneys.length}`,
    hasSearched && `Visible results: ${visibleJourneys.length}`,
    selectedFlightSummary && `Selected legs:\n${selectedFlightSummary}`,
    visibleResultsSummary && `Top visible results:\n${visibleResultsSummary}`,
    searchId && `Search ID: ${searchId}`,
    sessionId && `Session ID: ${sessionId}`,
    searchExpiry ? `Search expiry: ${searchExpiry}` : undefined,
  ])
}
